import { lazyMsg } from '~/common/localizationUtils'

import type { JSX } from 'react'

export enum ExpressionType {
  Number = 'number',
  Binding = 'binding',
  IfElse = 'if_else',
  Function = 'function',
  Condition = 'condition',
  Conditions = 'conditions',
  Round = 'round',
  String = 'string',
  List = 'list'
}

export enum FormulaTag {
  ClientAmount = 'client_amount',
  VendorAmount = 'vendor_amount',
  ClientRate = 'client_rate',
  VendorRate = 'vendor_rate',
  BillRate = 'bill_rate',
  MspFeeClient = 'msp_fee_client',
  VmsFeeClient = 'vms_fee_client',
  MspFeeClientRebate = 'msp_fee_client_rebate',
  MspFeeVendor = 'msp_fee_vendor',
  VmsFeeVendor = 'vms_fee_vendor',
  ServiceFee = 'service_fee',
  CompensationAmount = 'compensation_amount',
  NestedBillRate = 'nested_bill_rate'
}

export enum PayType {
  Hourly = 1,
  Daily = 2,
  Weekly = 3,
  Monthly = 5
}

export enum DisplayFormat {
  Currency = 'currency',
  Percentage = 'percentage',
  Choice = 'choice',
  Operator = 'operator'
}

export enum RoundingType {
  Currency = 'ROUND_CURRENCY',
  HalfEven = 'ROUND_HALF_EVEN',
  HalfUp = 'ROUND_HALF_UP',
  HalfDown = 'ROUND_HALF_DOWN'
}

export enum ProgramRateType {
  BillRateBased = 'bill_rate',
  PayRateBased = 'pay_rate'
}

export enum RoundingPrecision {
  CurrencyPrecision = -1,
  OneDecimal = 1,
  TwoDecimal = 2,
  ThreeDecimal = 3,
  FourDecimal = 4,
  FiveDecimal = 5,
  SixDecimal = 6,
  SevenDecimal = 7
}
export enum FormulaType {
  Formula = 'formula',
  Constant = 'constant',
  SystemVariable = 'system_variable'
}

export const FORMULA_VALIDATION_ERROR_CODES_MSG_MAP: Record<string, string> = {
  unbalanced_formula: 'calculation_engine_settings.formulas.validation_error.unbalanced_formula',
  ending_with_operator: 'calculation_engine_settings.formulas.validation_error.ending_with_operator',
  invalid_character: 'calculation_engine_settings.formulas.validation_error.invalid_character',
  unknown_formula: 'calculation_engine_settings.formulas.validation_error.unknown_formula',
  unknown_component: 'calculation_engine_settings.formulas.validation_error.unknown_component',
  recursive_formula_reference: 'calculation_engine_settings.formulas.validation_error.recursive_formula_reference',
  general_validation_error: 'calculation_engine_settings.formulas.validation_error.general_error',
  system_formula_reference_not_allowed:
    'calculation_engine_settings.formulas.validation_error.system_formula_reference_not_allowed',
  activation_deactivation_formula_with_associated_work_orders_jobs:
    'calculation_engine_settings.formulas.activation_deactivation_formula_with_associated_work_orders_jobs',
  missing_values_before_or_after_operator:
    'calculation_engine_settings.formulas.validation_error.missing_values_before_or_after_operator',
  parenthesis_touching: 'calculation_engine_settings.formulas.validation_error.parenthesis_touching',
  leading_zero: 'calculation_engine_settings.formulas.validation_error.leading_zero',
  formula_in_use: 'calculation_engine_settings.formulas.validation_error.formula_in_use',
  invalid_bill_rate_formula_reference:
    'calculation_engine_settings.formulas.validation_error.invalid_bill_rate_formula_reference',
  placeholder_reference_not_allowed:
    'calculation_engine_settings.formulas.validation_error.placeholder_reference_not_allowed',
  invalid_formula_placeholder: 'calculation_engine_settings.formulas.validation_error.invalid_formula_placeholder'
}

export const CALCULATION_PACK_VALIDATION_ERROR_CODES_MSG_MAP: Record<string, string> = {
  activation_deactivation_calculation_pack_with_associated_work_orders_jobs_rules:
    'calculation_engine_settings.activation_deactivation_calculation_pack_with_associated_work_orders_jobs_rules'
}

// Enum containing some of the system variables (only the ones we need to refer to in code)
export enum SystemVariables {
  FEE_TYPE = '8f9e8456-cf1e-4710-9591-6f4ef5944afe',
  WORK_ORDER_PREMIUM_RATE_MODIFIER_TYPE = '9d967382-239c-403b-82d3-5c6eb421ca1e',
  TIMESHEET_PREMIUM_RATE_MODIFIER_TYPE = 'c7e979c1-5a6c-4d65-9ada-fc0169ebb2fa',
  SHIFT_DIFFERENTIAL_RATE_MODIFIER_TYPE = '92c33517-20f0-42ff-86aa-578bfd897baf',
  CALENDAR_PAY_ENTRY_RATE_MODIFIER_TYPE = 'd0b4cc45-ed42-491d-8814-933547b1e2b5',
  WORK_TYPE_RATE_MODIFIER_TYPE = '33dc51bf-487c-4f7e-b5fc-351fa1853f1b',
  IS_WORK_TYPE_BREAK = '5e9fe0c7-a4dd-42c0-b7bd-d0dcb53acfb7',
  RATE_QUALIFICATION_STATUS = 'a83070e8-edee-4686-a537-3d5ff6f8c38b',
  WORK_TYPE_CLASSIFICATION = '2e81377b-0c76-4fe4-96d9-77ecbb3ebd52',
  USE_REGULAR_RATE = '30d0130f-5543-4988-a3cb-0b2a742d8b2c',
  TOTAL_COMPENSATION = '35b06687-e887-4e49-9dd0-cbe8e3de5e91',
  ENTRY_QUANTITY_UNIT = '1cac2a53-9c6c-46f6-abd9-a5dd01223fa1',
  QUANTITY_UNIT = 'f10ef873-a6e2-45ed-98b5-1e2157c0b175',
  TOTAL_REGULAR_WORKING_QUANTITY = '47fe05a6-727c-48f1-9d81-5bd2825347ca',
  TOTAL_WORKING_QUANTITY = 'a7238bd1-5e5c-4304-a638-e816cfbd62d4',
  QUANTITY = '52cf429f-c41c-4fc3-bbaa-67ff0378379f',
  VENDOR_ENTITY_ID = '482f2810-bfa1-47cb-b071-b5e17284686c',
  JOB_CATEGORY_ID = '7b58e46b-91aa-41b7-838d-b847e0b6a793',
  JOB_TEMPLATE_ID = '27027b0b-34b6-46bb-8fe2-9bc30cf841ad',
  CURRENCY_CODE = '48c3f8b3-9379-4433-8e63-1b744d335d27',
  LOCATION_ID = '6a36b7f0-9218-4b6d-af84-48097bdd0135',
  REGION_ID = '75642c53-5136-43ce-a973-45ae376bf746',
  COUNTRY_CODE = '41e7d1f4-5af5-4400-b3e4-ae91aed340e5',
  SUBDIVISION_CODE = '8cc56b38-d0f8-4086-bcf5-ca5a30d5a353',
  WORKER_CLASSIFICATION_ID = 'c9093796-59e1-4d7e-9670-1ffee36dec07',
  SOURCE_TYPE = 'b23887a3-8681-4b76-8037-de5e42cee100'
}

export function getSystemVariableValueType(value: string): 'string' | 'number' {
  if (
    value === SystemVariables.CURRENCY_CODE ||
    value === SystemVariables.COUNTRY_CODE ||
    value === SystemVariables.SUBDIVISION_CODE
  ) {
    return ExpressionType.String
  }
  return ExpressionType.Number
}

export enum SystemVariableSources {
  JOB_WO_RATE_CARD = 1,
  JOB_WO_RATE_CARD_DAILY_WEEKLY = 2,
  TIMESHEET = 3,
  PROGRAM_FEES = 4,
  PREMIUM_RATES = 5,
  INVOICE_PAYMENTS = 6,
  SHIFT_WORK_ORDER = 7,
  CALENDAR_PAY_PROFILE = 8,
  WORK_TYPE_PROFILE = 9,
  WORK_TYPE_PROFILE_WO_OVERRIDE = 10,
  OVERTIME_PROFILE_WORK_TYPE_PROFILE = 11,
  CONTRACTOR_TIMEKEEPING_SETTINGS = 12
}

export enum SystemVariableUsages {
  NULL = -1, // Not actually in the db, for filtering only
  FORMULA = 1,
  FORMULA_AND_CONDITION = 2,
  CONDITION = 3,
  DISPLAY_ONLY = 4,
  FORMULA_COMPONENT_CONDITIONS = 5
}

export enum SystemVariablePossibleValues {
  TRUE_FALSE = 1,
  MULTIPLIER_FLAT_RATE = 2,
  RATE_DIFFERENTIAL = 3,
  FEE_TYPE = 4,
  QUALIFIED_OR_NOT = 5,
  WORK_TYPE_CLASSIFICATION = 6
}

export enum FilterStatusEnum {
  INACTIVE = 0,
  ACTIVE = 1
}

interface SystemVariableLinkDict {
  label: JSX.Element
  link: string | null
}

export const SYSTEM_VARIABLE_SOURCES_MSG_CODES: Record<SystemVariableSources, SystemVariableLinkDict> = {
  [SystemVariableSources.JOB_WO_RATE_CARD]: {
    label: lazyMsg('calculation_engine_settings.source.job_wo_rate_card'),
    link: null
  },
  [SystemVariableSources.JOB_WO_RATE_CARD_DAILY_WEEKLY]: {
    label: lazyMsg('calculation_engine_settings.source.job_wo_rate_card_daily_weekly'),
    link: null
  },
  [SystemVariableSources.TIMESHEET]: {
    label: lazyMsg('calculation_engine_settings.source.timesheet'),
    link: null
  },
  [SystemVariableSources.PROGRAM_FEES]: {
    label: lazyMsg('calculation_engine_settings.source.program_fees'),
    link: '/fee_profile/program_fees/'
  },
  [SystemVariableSources.PREMIUM_RATES]: {
    label: lazyMsg('calculation_engine_settings.source.premium_rates'),
    link: '/premium_rates'
  },
  [SystemVariableSources.INVOICE_PAYMENTS]: {
    label: lazyMsg('calculation_engine_settings.source.invoice_payments'),
    link: '/entities/settings/invoice-and-payments/'
  },
  [SystemVariableSources.SHIFT_WORK_ORDER]: {
    label: lazyMsg('calculation_engine_settings.source.shift_work_order'),
    link: '/shifts/definitions'
  },
  [SystemVariableSources.CALENDAR_PAY_PROFILE]: {
    label: lazyMsg('calculation_engine_settings.source.calendar_pay_profile'),
    link: '/holidays/profile_settings/'
  },
  [SystemVariableSources.WORK_TYPE_PROFILE]: {
    label: lazyMsg('calculation_engine_settings.source.work_type_profile'),
    link: '/work_types/all/'
  },
  [SystemVariableSources.WORK_TYPE_PROFILE_WO_OVERRIDE]: {
    label: lazyMsg('calculation_engine_settings.source.work_type_profile_wo_override'),
    link: '/work_types/all/'
  },
  [SystemVariableSources.OVERTIME_PROFILE_WORK_TYPE_PROFILE]: {
    label: lazyMsg('calculation_engine_settings.source.overtime_profile_work_type_profile'),
    link: null
  },
  [SystemVariableSources.CONTRACTOR_TIMEKEEPING_SETTINGS]: {
    label: lazyMsg('calculation_engine_settings.source.contractor_timekeeping_settings'),
    link: null
  }
}

export const SYSTEM_VARIABLE_USAGES_MSG_CODES = {
  [SystemVariableUsages.NULL]: lazyMsg('calculation_engine_settings.usage.formula_and_conditions'),
  [SystemVariableUsages.FORMULA]: lazyMsg('calculation_engine_settings.usage.formula'),
  [SystemVariableUsages.FORMULA_AND_CONDITION]: lazyMsg('calculation_engine_settings.usage.formula_and_conditions'),
  [SystemVariableUsages.CONDITION]: lazyMsg('calculation_engine_settings.usage.conditions'),
  [SystemVariableUsages.DISPLAY_ONLY]: lazyMsg('calculation_engine_settings.usage.display_only'),
  [SystemVariableUsages.FORMULA_COMPONENT_CONDITIONS]: lazyMsg(
    'calculation_engine_settings.usage.formula_component_conditions'
  )
}

export const SYSTEM_VARIABLE_POSSIBLE_VALUES_MSG_CODES: Record<SystemVariablePossibleValues, JSX.Element> = {
  [SystemVariablePossibleValues.TRUE_FALSE]: lazyMsg('calculation_engine_settings.possible_values.true_false'),
  [SystemVariablePossibleValues.MULTIPLIER_FLAT_RATE]: lazyMsg(
    'calculation_engine_settings.usage.multiplier_flat_rate'
  ),
  [SystemVariablePossibleValues.RATE_DIFFERENTIAL]: lazyMsg('calculation_engine_settings.usage.rate_differential'),
  [SystemVariablePossibleValues.FEE_TYPE]: lazyMsg('calculation_engine_settings.usage.fee_type'),
  [SystemVariablePossibleValues.QUALIFIED_OR_NOT]: lazyMsg('calculation_engine_settings.usage.qualified_or_not'),
  [SystemVariablePossibleValues.WORK_TYPE_CLASSIFICATION]: lazyMsg(
    'calculation_engine_settings.usage.work_type_classification'
  )
}

export const MAP_SYSTEM_VARIABLE_TO_QUANTITY_UNIT_SOURCE: Record<string, SystemVariables> = {
  [SystemVariables.TOTAL_REGULAR_WORKING_QUANTITY]: SystemVariables.ENTRY_QUANTITY_UNIT,
  [SystemVariables.TOTAL_WORKING_QUANTITY]: SystemVariables.ENTRY_QUANTITY_UNIT,
  [SystemVariables.QUANTITY]: SystemVariables.QUANTITY_UNIT
}

export enum QuantityUnit {
  HOUR = 1,
  DAY = 2,
  WEEK = 3
}

export const MAP_QUANTITY_UNIT_TO_MSG: Record<number, string> = {
  [QuantityUnit.HOUR]: 'common.unit_of_measurement.hour',
  [QuantityUnit.DAY]: 'common.unit_of_measurement.day',
  [QuantityUnit.WEEK]: 'common.unit_of_measurement.week'
}
